export type RatingFactor = 'flavor' | 'construction' | 'burn' | 'value' | 'overall'

type NotesKeyFor<K extends RatingFactor> = K extends 'overall' ? 'overallNotes' : `${K}Notes`

export interface RatingFactorMeta<K extends RatingFactor = RatingFactor> {
  key: K
  label: string
  description: string
  lowLabel: string
  highLabel: string
  notesKey: NotesKeyFor<K>
}

export const RATING_FACTOR_META: [
  RatingFactorMeta<'flavor'>,
  RatingFactorMeta<'construction'>,
  RatingFactorMeta<'burn'>,
  RatingFactorMeta<'value'>,
] = [
  {
    key: 'flavor',
    label: 'Flavor',
    description: 'Taste complexity, balance, and enjoyment',
    lowLabel: 'Flat',
    highLabel: 'Complex',
    notesKey: 'flavorNotes',
  },
  {
    key: 'construction',
    label: 'Construction',
    description: 'Roll quality, draw, and physical attributes',
    lowLabel: 'Poor',
    highLabel: 'Perfect',
    notesKey: 'constructionNotes',
  },
  {
    key: 'burn',
    label: 'Burn',
    description: 'Evenness, temperature, and smoking characteristics',
    lowLabel: 'Uneven',
    highLabel: 'Razor',
    notesKey: 'burnNotes',
  },
  {
    key: 'value',
    label: 'Value',
    description: 'Price-to-quality ratio and perceived worth',
    lowLabel: 'Overpriced',
    highLabel: 'Bargain',
    notesKey: 'valueNotes',
  },
]

export function getScoreLabel(score: number, lowLabel: string, highLabel: string): string {
  if (score <= 3) return lowLabel
  if (score >= 8) return highLabel
  if (score <= 5) return 'Average'
  return 'Good'
}

/**
 * Default weights — must match DEFAULT_RATING_WEIGHTS in backend/src/services/ratingService.ts.
 * The preferences endpoint returns `defaults` so the UI can always display server-authoritative values;
 * this constant is only used for local previews before the first API response arrives.
 */
export const DEFAULT_RATING_WEIGHTS = {
  flavor: 0.3,
  construction: 0.2,
  burn: 0.25,
  value: 0.25,
} as const

export function calculateLocalOverall(
  factors: { flavor: number; construction: number; burn: number; value: number },
  weights: { flavor: number; construction: number; burn: number; value: number } = DEFAULT_RATING_WEIGHTS
): number {
  const weighted =
    factors.flavor * weights.flavor +
    factors.construction * weights.construction +
    factors.burn * weights.burn +
    factors.value * weights.value
  return Math.min(10, Math.max(1, Math.round(weighted)))
}
